import jsPDF from 'jspdf';
import logo from '../assets/logo_documentos.png.png';
import {
  NAVY, GRAY, BLACK,
  v, fmt, f,
  ul, field,
} from './pdfHelpers';

export interface ClinicalDocumentPDFParams {
  /** Título do cabeçalho, ex: "RECEITUÁRIO MÉDICO", "HIPÓTESES DIAGNÓSTICAS" */
  title: string;
  content: string;
  /** Se true, o corpo do texto é impresso em maiúsculas (usado na prescrição) */
  uppercase?: boolean;
  /** Prefixo do nome do arquivo salvo, ex: "receita", "diagnostico" */
  fileSlug: string;
  patientName?: string | null;
  patientCPF?: string | null;
  doctorName?: string | null;
  doctorCRM?: string | null;
  /** ISO date; default: hoje */
  date?: string | null;
}

const PAGE_H = 297; // A4, mm
const BOTTOM_MARGIN = 20;

export async function generateClinicalDocumentPDF(params: ClinicalDocumentPDFParams): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const LX = 15;  // margem esquerda
  const W  = 180; // largura útil

  // ── CABEÇALHO ────────────────────────────────────────────────────────────────
  let y = 10;
  const logoH = 24;
  const logoW = logoH * (643/388); // proporção real de logo_documentos.png.png
  doc.addImage(logo, 'PNG', (210-logoW)/2, y, logoW, logoH);
  y += 30;

  ul(doc, LX, y, W); y += 8;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(15);
  doc.text(params.title, 105, y, { align:'center' });

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(8.5);
  doc.text('DATA:', LX+W-38, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(fmt(params.date ?? new Date().toISOString()), LX+W-25, y);

  y += 4; ul(doc, LX, y, W); y += 14;

  // ── PACIENTE ───────────────────────────────────────────────────────────────────
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('Paciente:', LX, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','bold'); doc.setFontSize(10.5);
  doc.text(v(params.patientName) || '—', LX+22, y);

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('CPF:', LX+W-45, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(v(params.patientCPF) || '—', LX+W-33, y);

  y += 4; ul(doc, LX, y, W);
  y += 16;

  // ── CORPO (com paginação) ───────────────────────────────────────────────────────
  const TEXT_MARGIN = 4; // mesma margem interna à esquerda e à direita
  const textX = LX + TEXT_MARGIN;
  const textW = W - TEXT_MARGIN * 2;
  const lineH = 6.5;

  // a fonte precisa estar definida ANTES do splitTextToSize, senão a quebra de linha
  // é calculada com a métrica errada e o texto real (desenhado depois) estoura a margem
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);

  const rawContent = v(params.content);
  const lines = doc.splitTextToSize(params.uppercase ? rawContent.toUpperCase() : rawContent, textW);

  lines.forEach((line: string) => {
    if (y > PAGE_H - BOTTOM_MARGIN) {
      doc.addPage();
      y = 20;
    }
    doc.text(line, textX, y);
    y += lineH;
  });

  if (lines.length === 0) {
    f(doc,'text',GRAY); doc.setFontSize(10.5);
    doc.text('—', textX, y);
    y += lineH;
  }

  // ── ASSINATURA / RODAPÉ ──────────────────────────────────────────────────────────
  const sigY = Math.max(y + 15, PAGE_H - 45);
  if (sigY > PAGE_H - BOTTOM_MARGIN) {
    doc.addPage();
  }
  const finalSigY = sigY > PAGE_H - BOTTOM_MARGIN ? PAGE_H - 40 : sigY;

  const sigW = 90;
  const sigX = LX + (W - sigW) / 2;
  ul(doc, sigX, finalSigY, sigW);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('Assinatura e Carimbo do Médico', sigX+sigW/2, finalSigY+3, { align:'center' });
  field(doc, 'Médico:', v(params.doctorName), sigX, finalSigY+9, sigW/2-2);
  field(doc, 'CRM:', v(params.doctorCRM), sigX+sigW/2+2, finalSigY+9, sigW/2-2);

  const FY = finalSigY + 20;
  ul(doc, LX, FY, W);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6);
  doc.text(
    'Rua Exemplo, 123 - Centro - Sua Cidade/UF - CEP 00000-000     (00) 0000-0000     www.sagradavisao.com.br',
    105, FY+4, { align:'center' },
  );

  // ── SALVAR ────────────────────────────────────────────────────────────────────
  const name     = v(params.patientName ?? 'paciente');
  const datePart = fmt(params.date ?? new Date().toISOString()).replace(/\//g,'-');
  doc.save(`${params.fileSlug}_${name.replace(/\s+/g,'_').toLowerCase()}_${datePart}.pdf`);
}
